'use client';

import React, { useState, createContext, useContext } from 'react';

import { Box, BoxProps } from './box';
import { Badge } from './badge';
import { Spinner } from './spinner';

/**
 * Tabs Context - Manages tab state across compound components
 */
interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
  variant: 'line' | 'enclosed' | 'soft-rounded';
  orientation: 'horizontal' | 'vertical';
  colorPalette: 'brand' | 'blue' | 'green' | 'red' | 'orange' | 'purple';
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

/**
 * Hook to access Tabs context
 * @throws Error if used outside TabsRoot
 */
const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs compound components must be used within Tabs.Root');
  }
  return context;
};

/**
 * Root Tabs Component
 * Provides the main container for a compound Tabs system with Chakra UI v3 semantic tokens
 *
 * @example
 * ```tsx
 * <Tabs.Root defaultValue="tab1" variant="line" colorPalette="brand">
 *   <Tabs.List>
 *     <Tabs.Trigger value="tab1">Tab 1</Tabs.Trigger>
 *     <Tabs.Trigger value="tab2">Tab 2</Tabs.Trigger>
 *   </Tabs.List>
 *   <Tabs.ContentGroup>
 *     <Tabs.Content value="tab1">Content 1</Tabs.Content>
 *     <Tabs.Content value="tab2">Content 2</Tabs.Content>
 *   </Tabs.ContentGroup>
 * </Tabs.Root>
 * ```
 */
export interface TabsRootProps extends BoxProps {
  /** Currently selected tab value (controlled) */
  value?: string;
  /** Initial tab value for uncontrolled usage */
  defaultValue?: string;
  /** Callback when tab selection changes */
  onValueChange?: (value: string) => void;
  /** Visual style variant */
  variant?: 'line' | 'enclosed' | 'soft-rounded';
  /** Layout orientation */
  orientation?: 'horizontal' | 'vertical';
  /** Color palette for active state (AxonFleet brand by default) */
  colorPalette?: 'brand' | 'blue' | 'green' | 'red' | 'orange' | 'purple';
  /** Child components (Tabs.List, Tabs.ContentGroup) */
  children: React.ReactNode;
}

const TabsRoot = React.forwardRef<HTMLDivElement, TabsRootProps>(
  (
    {
      value: controlledValue,
      defaultValue = '',
      onValueChange,
      variant = 'line',
      orientation = 'horizontal',
      colorPalette = 'brand',
      children,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState(controlledValue || defaultValue);
    const value = controlledValue !== undefined ? controlledValue : internalValue;

    const handleValueChange = (newValue: string) => {
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    };

    const contextValue: TabsContextValue = {
      value,
      onValueChange: handleValueChange,
      variant,
      orientation,
      colorPalette,
    };

    return (
      <TabsContext.Provider value={contextValue}>
        <Box
          ref={ref}
          display="flex"
          flexDirection={orientation === 'vertical' ? 'row' : 'column'}
          {...props}
        >
          {children}
        </Box>
      </TabsContext.Provider>
    );
  }
);

TabsRoot.displayName = 'TabsRoot';

/**
 * Tabs List Component
 * Container for tab triggers with variant-specific styling
 * Uses Chakra UI v3 semantic tokens for automatic dark mode support
 */
export interface TabsListProps extends BoxProps {
  /** Tab trigger components */
  children: React.ReactNode;
}

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(({ children, ...props }, ref) => {
  const { variant, orientation } = useTabsContext();

  const baseStyles: BoxProps = {
    display: 'flex',
    flexDirection: orientation === 'vertical' ? 'column' : 'row',
    alignItems: orientation === 'vertical' ? 'stretch' : 'center',
    gap: 2, // Default spacing between tabs for better UX
  };

  // Chakra UI v3 semantic tokens - automatic dark mode via CSS variables
  const variantStyles: Record<string, BoxProps> = {
    line: {
      borderBottom: orientation === 'horizontal' ? '1px solid' : 'none',
      borderRight: orientation === 'vertical' ? '1px solid' : 'none',
      borderColor: 'gray.200',
    },
    enclosed: {
      bg: 'gray.50',
      borderRadius: 'md',
      padding: 1,
    },
    'soft-rounded': {
      bg: 'gray.50',
      borderRadius: 'lg',
      padding: 1,
    },
  };

  return (
    <Box
      ref={ref}
      role="tablist"
      aria-orientation={orientation}
      {...baseStyles}
      {...variantStyles[variant]}
      {...props}
    >
      {children}
    </Box>
  );
});

TabsList.displayName = 'TabsList';

/**
 * Tabs Trigger Component
 * Individual tab button with WCAG AAA touch-friendly design (44x44px minimum)
 * Supports keyboard navigation, badges, and icons
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
 *
 * // With badge
 * <Tabs.Trigger value="orders" badge={12} badgeColorPalette="blue">
 *   Orders
 * </Tabs.Trigger>
 *
 * // With icon
 * <Tabs.Trigger value="settings" icon={<LuSettings />}>
 *   Settings
 * </Tabs.Trigger>
 * ```
 */
export interface TabsTriggerProps extends Omit<BoxProps, 'as'> {
  /** Unique identifier for this tab */
  value: string;
  /** Tab label content */
  children: React.ReactNode;
  /** Whether the tab is disabled */
  disabled?: boolean;
  /**
   * Whether to enforce minimum 44x44px touch target for accessibility
   * @default true
   */
  touchFriendly?: boolean;
  /** Badge counter or label to display next to tab text */
  badge?: number | string;
  /** Color palette for badge */
  badgeColorPalette?: 'blue' | 'green' | 'red' | 'orange' | 'purple' | 'gray';
  /** Icon to display with tab */
  icon?: React.ReactNode;
  /** Position of icon relative to text */
  iconPosition?: 'left' | 'top';
}

const TabsTrigger = React.forwardRef<HTMLDivElement, TabsTriggerProps>(
  (
    {
      value,
      children,
      disabled = false,
      touchFriendly = true,
      badge,
      badgeColorPalette = 'blue',
      icon,
      iconPosition = 'left',
      ...props
    },
    ref
  ) => {
    const {
      value: currentValue,
      onValueChange,
      variant,
      orientation,
      colorPalette,
    } = useTabsContext();
    const isSelected = currentValue === value;

    const handleClick = () => {
      if (!disabled) {
        onValueChange(value);
      }
    };

    /**
     * Keyboard navigation handler for WCAG 2.1 AAA compliance
     * Arrow keys: Navigate between tabs
     * Home: Jump to first tab
     * End: Jump to last tab
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      // Find the tablist container (not parent element to avoid wrapper issues)
      const tabList = (
        e.currentTarget.closest('[role="tablist"]') as HTMLElement
      )?.querySelectorAll('[role="tab"]:not([disabled])');

      if (!tabList || tabList.length === 0) return;

      const currentIndex = Array.from(tabList).findIndex((tab) => tab === e.currentTarget);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;

      switch (e.key) {
        case 'ArrowRight':
          if (orientation === 'horizontal') {
            e.preventDefault();
            nextIndex = (currentIndex + 1) % tabList.length;
          }
          break;
        case 'ArrowLeft':
          if (orientation === 'horizontal') {
            e.preventDefault();
            nextIndex = (currentIndex - 1 + tabList.length) % tabList.length;
          }
          break;
        case 'ArrowDown':
          if (orientation === 'vertical') {
            e.preventDefault();
            nextIndex = (currentIndex + 1) % tabList.length;
          }
          break;
        case 'ArrowUp':
          if (orientation === 'vertical') {
            e.preventDefault();
            nextIndex = (currentIndex - 1 + tabList.length) % tabList.length;
          }
          break;
        case 'Home':
          e.preventDefault();
          nextIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          nextIndex = tabList.length - 1;
          break;
        default:
          return;
      }

      const nextTab = tabList[nextIndex] as HTMLElement;
      nextTab?.focus();
      nextTab?.click();
    };

    // Chakra UI v3 semantic color palette tokens
    const palette = colorPalette === 'brand' ? 'brand' : colorPalette;

    // Base styles using Chakra UI v3 semantic tokens
    const baseStyles: BoxProps = {
      padding: 3,
      fontSize: 'sm',
      fontWeight: 'medium',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      border: 'none',
      bg: 'transparent',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 'fit-content',
    };

    // Variant-specific styles with semantic tokens
    const variantStyles: Record<string, BoxProps> = {
      line: {
        color: isSelected ? `${palette}.600` : 'gray.600',
        borderBottom: orientation === 'horizontal' && isSelected ? '2px solid' : 'none',
        borderRight: orientation === 'vertical' && isSelected ? '2px solid' : 'none',
        borderColor: isSelected ? `${palette}.600` : 'transparent',
        marginBottom: orientation === 'horizontal' ? '-1px' : '0',
        marginRight: orientation === 'vertical' ? '-1px' : '0',
        _hover: !disabled ? { color: `${palette}.600` } : {},
      },
      enclosed: {
        bg: isSelected ? 'white' : 'transparent',
        color: isSelected ? 'gray.900' : 'gray.600',
        borderRadius: 'md',
        border: '1px solid',
        borderColor: isSelected ? 'gray.200' : 'transparent',
        _hover: !disabled ? { bg: isSelected ? 'white' : 'gray.100' } : {},
      },
      'soft-rounded': {
        bg: isSelected ? 'white' : 'transparent',
        color: isSelected ? 'gray.900' : 'gray.600',
        borderRadius: 'lg',
        _hover: !disabled ? { bg: isSelected ? 'white' : 'gray.100' } : {},
      },
    };

    // Touch-friendly logic: Ensure 44x44px minimum (WCAG 2.1 AAA)
    const minTouchSize = 44;
    const estimatedHeight = 40;
    const needsTouchPadding = touchFriendly && estimatedHeight < minTouchSize;
    const touchPadding = needsTouchPadding ? (minTouchSize - estimatedHeight) / 2 : 0;

    // Compose tab content with icon, text, and badge
    const renderTabContent = () => {
      const content = (
        <>
          {icon && iconPosition === 'left' && (
            <Box as="span" display="inline-flex" marginRight={2} flexShrink={0}>
              {icon}
            </Box>
          )}
          {icon && iconPosition === 'top' ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
              <Box as="span" display="inline-flex" flexShrink={0}>
                {icon}
              </Box>
              <Box as="span">{children}</Box>
            </Box>
          ) : (
            <Box as="span">{children}</Box>
          )}
          {badge !== undefined && badge !== null && badge !== '' && (
            <Box as="span" marginLeft={2}>
              <Badge colorPalette={badgeColorPalette} size="sm">
                {badge}
              </Badge>
            </Box>
          )}
        </>
      );

      return icon && iconPosition === 'top' ? (
        content
      ) : (
        <Box display="inline-flex" alignItems="center">
          {content}
        </Box>
      );
    };

    const tabElement = (
      <Box
        as="button"
        ref={ref}
        role="tab"
        // Prevent form submission — default button type is "submit" in HTML spec
        {...({ type: 'button' } as Record<string, string>)}
        aria-selected={isSelected}
        aria-disabled={disabled}
        tabIndex={isSelected ? 0 : -1}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...baseStyles}
        {...variantStyles[variant]}
        {...props}
        {...(disabled ? { disabled: true } : {})}
      >
        {renderTabContent()}
      </Box>
    );

    // Wrap with touch-friendly padding if needed
    if (needsTouchPadding) {
      return (
        <Box as="span" display="inline-block" position="relative" padding={`${touchPadding}px`}>
          {tabElement}
          <Box as="span" position="absolute" inset={0} pointerEvents="none" aria-hidden="true" />
        </Box>
      );
    }

    return tabElement;
  }
);

TabsTrigger.displayName = 'TabsTrigger';

/**
 * Tabs Content Group Component
 * Container for all tab content panels
 */
export interface TabsContentGroupProps extends BoxProps {
  /** Tab content components */
  children: React.ReactNode;
}

const TabsContentGroup = React.forwardRef<HTMLDivElement, TabsContentGroupProps>(
  ({ children, ...props }, ref) => {
    return (
      <Box ref={ref} {...props}>
        {children}
      </Box>
    );
  }
);

TabsContentGroup.displayName = 'TabsContentGroup';

/**
 * Tabs Content Component
 * Individual content panel that displays when its corresponding tab is selected
 * Supports loading state with spinner
 *
 * @example
 * ```tsx
 * <Tabs.Content value="tab1" loading={isLoading}>
 *   <p>Content here...</p>
 * </Tabs.Content>
 * ```
 */
export interface TabsContentProps extends BoxProps {
  /** Value matching the corresponding Tabs.Trigger */
  value: string;
  /** Content to display when tab is active */
  children: React.ReactNode;
  /** Whether content is loading */
  loading?: boolean;
}

const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(
  ({ value, children, loading = false, ...props }, ref) => {
    const { value: currentValue } = useTabsContext();
    const isSelected = currentValue === value;

    if (!isSelected) {
      return null;
    }

    return (
      <Box ref={ref} role="tabpanel" aria-labelledby={`tab-${value}`} paddingTop={4} {...props}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" padding={8}>
            <Spinner size="lg" />
          </Box>
        ) : (
          children
        )}
      </Box>
    );
  }
);

TabsContent.displayName = 'TabsContent';

/**
 * Tabs Indicator Component
 * Optional visual enhancement for line variant - animated underline indicator
 * Only renders for line variant, uses semantic tokens for dark mode
 *
 * @example
 * ```tsx
 * <Tabs.Root variant="line">
 *   <Tabs.List>
 *     <Tabs.Trigger value="tab1">Tab 1</Tabs.Trigger>
 *     <Tabs.Indicator />
 *   </Tabs.List>
 * </Tabs.Root>
 * ```
 */
export interface TabsIndicatorProps extends BoxProps {
  /** Optional children (rarely used) */
  children?: React.ReactNode;
}

const TabsIndicator = React.forwardRef<HTMLDivElement, TabsIndicatorProps>(
  ({ children, ...props }, ref) => {
    const { variant, colorPalette } = useTabsContext();

    // Only render for line variant as an optional enhancement
    if (variant !== 'line') {
      return null;
    }

    // Chakra UI v3 semantic color palette token
    const palette = colorPalette === 'brand' ? 'brand' : colorPalette;

    return (
      <Box
        ref={ref}
        position="absolute"
        bottom={0}
        left={0}
        height="2px"
        bg={`${palette}.600`}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        borderRadius="full"
        {...props}
      >
        {children}
      </Box>
    );
  }
);

TabsIndicator.displayName = 'TabsIndicator';

/**
 * Compound component exports for Tabs.* pattern
 */
export const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
  ContentGroup: TabsContentGroup,
  Indicator: TabsIndicator,
};

/**
 * Individual exports for direct usage
 */
export { TabsRoot, TabsList, TabsTrigger, TabsContent, TabsContentGroup, TabsIndicator };
